/**
 * Inbox — Screen 3
 * 3-pane on desktop: conversation list / thread / detail rail
 * Single-pane on mobile: list → thread (with back button)
 *
 * Fixes applied in this version:
 *  - BLOCKER #2: Mobile single-pane with ← back button
 *  - BLOCKER #3: Rules of Hooks — all useState/useMutation at top of ThreadEventRow
 *  - MAJOR #4: Timestamps use shared fmtTime(iso, timezone) — NO raw ISO fallback
 *  - MAJOR #8: Feedback persisted via PATCH /api/thread-events/:id/feedback
 *  - MAJOR #9: Stage chip replaced with interactive <select>
 */

import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchLeads,
  fetchLead,
  patchLead,
  fetchSettings,
  patchThreadEventFeedback,
  type FeedbackValue,
} from '../../lib/apiHelpers.js';
import { fmtTime, fmtRelative } from '../../lib/time.js';
import { useIsMobile } from '../../lib/hooks.js';
import { AvatarInitials } from '../../components/AvatarInitials.js';
import { AiToggle } from '../../components/Toggle.js';
import { StageChip, STAGE_CONFIG } from '../../components/StageChip.js';
import { ComingSoon } from '../../components/ComingSoon.js';
import { AiOrb } from '../../components/AiOrb.js';
import { Waveform } from '../../components/Waveform.js';
import type { ApiLead, ApiThreadEvent, LeadStage } from '@contactrate/shared';

const CHANNEL_COLOR: Record<string, string> = {
  imessage: '#0B84FE',
  rcs: '#21A254',
  sms: '#21A254',
  voice: '#D97706',
};

const ALL_STAGES = Object.keys(STAGE_CONFIG) as LeadStage[];

// ── Stage select (interactive replacement for read-only chip) ─────────────────

function StageSelect({
  leadId,
  stage,
  onChanged,
}: {
  leadId: string;
  stage: LeadStage;
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const cfg = STAGE_CONFIG[stage] ?? STAGE_CONFIG['new'];

  const mutation = useMutation({
    mutationFn: (newStage: LeadStage) => patchLead(leadId, { stage: newStage }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
      onChanged?.();
    },
  });

  return (
    <select
      value={stage}
      onChange={(e) => mutation.mutate(e.target.value as LeadStage)}
      disabled={mutation.isPending}
      style={{
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: 999,
        padding: '2px 24px 2px 10px',
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        appearance: 'auto',
        outline: 'none',
        transition: 'opacity 0.15s',
        opacity: mutation.isPending ? 0.6 : 1,
      }}
    >
      {ALL_STAGES.map((s) => (
        <option key={s} value={s}>
          {STAGE_CONFIG[s].label}
        </option>
      ))}
    </select>
  );
}

// ── Conversation list ─────────────────────────────────────────────────────────

type InboxTab = 'unread' | 'all' | 'needs-you' | 'booked';

function ConvList({
  selectedId,
  onSelect,
  timezone,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
  timezone: string;
}) {
  const [tab, setTab] = useState<InboxTab>('all');
  const [search, setSearch] = useState('');

  const { data } = useQuery({
    queryKey: ['leads', { pageSize: 50 }],
    queryFn: () => fetchLeads({ pageSize: 50 }),
  });

  const leads = data?.data ?? [];

  const filtered = leads.filter((l) => {
    if (tab === 'unread') return l.unread;
    if (tab === 'needs-you') return l.needsYou;
    if (tab === 'booked') return l.stage === 'booked';
    return true;
  }).filter((l) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (l.firstName?.toLowerCase().includes(q) ?? false) ||
      (l.lastName?.toLowerCase().includes(q) ?? false) ||
      l.phone.includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.needsYou !== b.needsYou) return a.needsYou ? -1 : 1;
    if (a.unread !== b.unread) return a.unread ? -1 : 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const tabCounts = {
    unread: leads.filter((l) => l.unread).length,
    'needs-you': leads.filter((l) => l.needsYou).length,
  };

  const TABS: { key: InboxTab; label: string; count?: number }[] = [
    { key: 'unread', label: 'Unread', count: tabCounts.unread },
    { key: 'all', label: 'All' },
    { key: 'needs-you', label: 'Needs you', count: tabCounts['needs-you'] },
    { key: 'booked', label: 'Booked' },
  ];

  return (
    <div className="flex flex-col h-full" style={{ borderRight: '1px solid #F0F3F8' }}>
      {/* Search */}
      <div className="p-3" style={{ borderBottom: '1px solid #F0F3F8' }}>
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-[10px]"
          style={{ background: '#F2F4F7', border: '1px solid #E5EAF2' }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted flex-shrink-0">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search conversations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-[12.5px] text-ink placeholder-muted outline-none min-w-0"
          />
        </div>
      </div>

      {/* Live strip */}
      <button
        className="mx-3 my-2 px-3 py-1.5 rounded-[8px] text-[11px] font-medium text-left transition-colors"
        style={{ background: 'rgba(21,150,98,0.07)', color: '#159662', border: '1px solid rgba(21,150,98,0.15)' }}
      >
        6 calls · 2 texts live now →
      </button>

      {/* Tab bar */}
      <div className="flex" style={{ borderBottom: '2px solid #F0F3F8' }}>
        {TABS.map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="flex-1 flex items-center justify-center gap-1 pb-2 pt-1.5 text-[11.5px] font-medium transition-colors relative"
            style={{ color: tab === key ? '#0D1729' : '#64748B' }}
          >
            {label}
            {count != null && count > 0 && (
              <span
                className="text-[9px] font-semibold text-white rounded-chip px-1 py-0.5"
                style={{
                  background: key === 'needs-you' ? '#D97706' : '#2E66E0',
                  fontFamily: '"DM Mono", monospace',
                }}
              >
                {count}
              </span>
            )}
            {tab === key && (
              <span
                className="absolute bottom-[-2px] left-0 right-0 h-0.5 rounded-full"
                style={{ background: '#0D1729' }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Conversation rows */}
      <div className="flex-1 overflow-y-auto">
        {sorted.map((lead) => {
          const name = `${lead.firstName} ${lead.lastName}`;
          const isSelected = lead.id === selectedId;

          return (
            <button
              key={lead.id}
              onClick={() => onSelect(lead.id)}
              className="w-full text-left px-3 py-3 flex items-start gap-2.5 transition-colors relative"
              style={{
                background: lead.needsYou
                  ? 'rgba(251,191,36,0.09)'
                  : isSelected
                    ? 'rgba(46,102,224,0.06)'
                    : undefined,
                borderLeft: lead.needsYou
                  ? '2px solid #D97706'
                  : isSelected
                    ? '2px solid #2E66E0'
                    : '2px solid transparent',
                borderBottom: '1px solid #F0F3F8',
              }}
            >
              <AvatarInitials name={name} size={34} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span
                    className="text-[12.5px] truncate mr-2"
                    style={{ fontWeight: lead.unread ? 700 : 500, color: '#0D1729' }}
                  >
                    {name}
                  </span>
                  <span
                    className="text-[10px] text-muted flex-shrink-0"
                    style={{ fontFamily: '"DM Mono", monospace' }}
                  >
                    {fmtRelative(lead.updatedAt, timezone)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <StageChip stage={lead.stage as LeadStage} size="sm" />
                  {lead.unread && (
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: lead.needsYou ? '#D97706' : '#2E66E0' }}
                    />
                  )}
                </div>
              </div>
            </button>
          );
        })}

        {sorted.length === 0 && (
          <div className="p-6 text-center text-[12.5px] text-muted">No conversations found</div>
        )}
      </div>
    </div>
  );
}

// ── Thread events ─────────────────────────────────────────────────────────────

function ThreadEventRow({
  ev,
  leadId,
  aiName,
  timezone,
}: {
  ev: ApiThreadEvent;
  leadId: string;
  aiName: string;
  timezone: string;
}) {
  // ── ALL hooks at top — before any early return (Rules of Hooks) ───────────
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  // Single feedback state used across ALL event-type branches below
  const [localFeedback, setLocalFeedback] = useState<FeedbackValue>(
    ev.feedback as FeedbackValue ?? null,
  );

  const feedbackMutation = useMutation({
    mutationFn: (value: FeedbackValue) => patchThreadEventFeedback(ev.id, value),
    onMutate: (value) => {
      // Optimistic update
      setLocalFeedback(value);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
    },
    onError: () => {
      // Revert on error
      setLocalFeedback(ev.feedback as FeedbackValue ?? null);
    },
  });

  const toggleFeedback = (value: 'up' | 'down') => {
    const next: FeedbackValue = localFeedback === value ? null : value;
    feedbackMutation.mutate(next);
  };

  // ── NOW safe to use early returns ─────────────────────────────────────────

  const payload = ev.payload as Record<string, unknown> | null;

  if (ev.kind === 'day') {
    return (
      <div className="flex items-center justify-center py-3">
        <span
          className="px-3 py-1 rounded-chip text-[10px] font-medium text-muted"
          style={{ background: '#F2F4F7', border: '1px solid #E5EAF2' }}
        >
          {payload?.label as string ?? 'Today'} — times in {timezone}
        </span>
      </div>
    );
  }

  if (ev.kind === 'system') {
    const icon = payload?.icon as string | undefined;
    const sysColor =
      icon === 'flag' ? '#DB2777' :
        icon === 'close' ? '#159662' :
          icon === 'book' ? '#D97706' :
            '#6D4AEA';

    return (
      <div className="flex items-center justify-center py-2">
        <span
          className="px-3 py-1.5 rounded-chip text-[11px] font-medium"
          style={{
            background: `${sysColor}10`,
            color: sysColor,
            border: `1px solid ${sysColor}30`,
          }}
        >
          {ev.body ?? payload?.text as string ?? 'System event'}
        </span>
      </div>
    );
  }

  if (ev.kind === 'call') {
    const callPayload = payload as {
      outcome?: string;
      duration_s?: number;
      transcript?: { who: string; text: string }[];
    } | null;
    const outcome = callPayload?.outcome ?? 'connected';
    const durationS = callPayload?.duration_s ?? 0;
    const transcript = callPayload?.transcript ?? [];
    const isLive = outcome === 'live';

    const outcomeConfig: Record<string, { color: string; label: string }> = {
      connected: { color: '#159662', label: 'Connected' },
      no_answer: { color: '#64748B', label: 'No answer' },
      busy: { color: '#64748B', label: 'Busy' },
      dq: { color: '#EF4444', label: 'DQ' },
      transferred: { color: '#6D4AEA', label: 'Transferred' },
      live: { color: '#159662', label: 'Live now' },
      callback: { color: '#D97706', label: 'Callback' },
      voicemail: { color: '#64748B', label: 'Voicemail' },
      failed: { color: '#EF4444', label: 'Failed' },
    };
    const oc = outcomeConfig[outcome] ?? { color: '#64748B', label: outcome };
    const m = Math.floor(durationS / 60);
    const s = durationS % 60;
    const durStr = durationS > 0 ? `${m}:${String(s).padStart(2, '0')}` : '—';

    return (
      <div className="bg-surface rounded-card border border-border p-4 flex flex-col gap-3 my-1">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `${oc.color}18` }}
          >
            {isLive ? (
              <Waveform color={oc.color} barCount={4} height={12} />
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={oc.color} strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.92 11.5 19.79 19.79 0 0 1 1.97 2.18 2 2 0 0 1 3.95 0h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 7.91a16 16 0 0 0 5.99 5.99l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            )}
          </div>
          <div className="flex-1">
            <p className="text-[12.5px] font-semibold text-ink">
              <span className="text-violet-DEFAULT">✦</span> {aiName} called
            </p>
            <p
              className="text-[10px] text-muted"
              style={{ fontFamily: '"DM Mono", monospace' }}
            >
              outbound · {fmtTime(ev.createdAt, timezone)} · {durationS > 0 ? durStr : '—'}
            </p>
          </div>
          <span
            className="px-2 py-0.5 rounded-chip text-[10px] font-semibold"
            style={{ background: `${oc.color}18`, color: oc.color }}
          >
            {oc.label} {durationS > 0 ? durStr : ''}
          </span>
        </div>

        {/* Playback bar */}
        {!isLive && durationS > 0 && (
          <div
            className="flex items-center gap-2 p-2 rounded-[8px]"
            style={{ background: '#FAFBFC', border: '1px solid #F0F3F8' }}
          >
            <button
              onClick={() => setPlaying((p) => !p)}
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              style={{ background: '#159662' }}
            >
              {playing ? (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                  <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              )}
            </button>
            <div className="flex-1 rounded-full overflow-hidden" style={{ height: 5, background: '#E5EAF2' }}>
              <div className="h-full rounded-full" style={{ width: '35%', background: '#159662' }} />
            </div>
            <span className="text-[10px] text-muted" style={{ fontFamily: '"DM Mono", monospace' }}>
              0:00 / {durStr}
            </span>
          </div>
        )}

        {/* Transcript */}
        {transcript.length > 0 && (
          <div>
            <div className="flex flex-col gap-1 mb-2">
              {(expanded ? transcript : transcript.slice(-2)).map((line, i) => (
                <p key={i} className="text-[12px] text-ink">
                  <span className="font-semibold" style={{ color: '#6D4AEA' }}>{line.who}</span>{' '}
                  — {line.text}
                </p>
              ))}
            </div>
            {transcript.length > 2 && (
              <button
                onClick={() => setExpanded((e) => !e)}
                className="text-[11px] font-semibold text-blue-DEFAULT hover:text-blue-hover transition-colors"
              >
                {expanded
                  ? 'Collapse transcript ↑'
                  : `Full transcript (${transcript.length} lines) →`}
              </button>
            )}
          </div>
        )}

        {/* Feedback */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleFeedback('up')}
            className="text-[14px] transition-opacity"
            style={{ opacity: localFeedback === 'up' ? 1 : 0.35 }}
          >
            👍
          </button>
          <button
            onClick={() => toggleFeedback('down')}
            className="text-[14px] transition-opacity"
            style={{ opacity: localFeedback === 'down' ? 1 : 0.35 }}
          >
            👎
          </button>
        </div>
      </div>
    );
  }

  if (ev.kind === 'suggestion') {
    const draft = payload?.draft as string | undefined;
    return (
      <div
        className="rounded-card p-4 my-1 flex flex-col gap-3"
        style={{ border: '1px solid rgba(217,119,6,0.3)', background: 'rgba(251,191,36,0.04)' }}
      >
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em]" style={{ color: '#B45309' }}>
          ✦ {aiName} suggests — outside script
        </p>
        {draft && (
          <div
            className="px-3 py-2 rounded-[8px] text-[12.5px] text-ink"
            style={{ background: '#F2F4F7' }}
          >
            {draft}
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-white"
            style={{ background: '#2E66E0' }}
          >
            Send this reply
          </button>
          <button
            className="px-3 py-1.5 rounded-btn text-[11.5px] font-medium text-ink"
            style={{ background: '#F2F4F7' }}
          >
            Edit first
          </button>
        </div>
      </div>
    );
  }

  // Text bubbles: in | ai | human
  const isInbound = ev.kind === 'in';
  const isAi = ev.kind === 'ai';
  const channelColor = isAi ? '#0B84FE' : isInbound ? undefined : '#21A254';

  return (
    <div className={`flex flex-col gap-0.5 my-0.5 ${isInbound ? 'items-start' : 'items-end'}`}>
      <div
        className="max-w-[70%] px-3 py-2 text-[13.5px]"
        style={{
          background: isInbound ? '#E9E9EB' : channelColor,
          color: isInbound ? '#0D1729' : 'white',
          borderRadius: isInbound ? '18px 18px 18px 5px' : '18px 18px 5px 18px',
        }}
      >
        {ev.body}
      </div>
      <div className={`flex items-center gap-1 ${isInbound ? '' : 'flex-row-reverse'}`}>
        <p
          className="text-[9.5px] text-muted px-1"
          style={{ fontFamily: '"DM Mono", monospace' }}
        >
          {isAi ? `✦ ${aiName} · iMessage · ` : isInbound ? '' : 'Dan (you) · '}
          {fmtTime(ev.createdAt, timezone)}
        </p>
        {(isAi || ev.kind === 'human') && (
          <div className="flex gap-0.5">
            <button
              onClick={() => toggleFeedback('up')}
              className="text-[11px] transition-opacity"
              style={{ opacity: localFeedback === 'up' ? 1 : 0.3 }}
            >
              👍
            </button>
            <button
              onClick={() => toggleFeedback('down')}
              className="text-[11px] transition-opacity"
              style={{ opacity: localFeedback === 'down' ? 1 : 0.3 }}
            >
              👎
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Thread pane ──────────────────────────────────────────────────────────────

function ThreadPane({
  leadId,
  aiName,
  showRail,
  onToggleRail,
  onBack,
  timezone,
}: {
  leadId: string;
  aiName: string;
  showRail: boolean;
  onToggleRail: () => void;
  onBack?: () => void;
  timezone: string;
}) {
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: lead } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => fetchLead(leadId),
  });

  const toggleAi = useMutation({
    mutationFn: (ai: boolean) => patchLead(leadId, { ai_paused: !ai }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lead', leadId] });
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lead?.threadEvents]);

  if (!lead) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <AiOrb size={32} />
      </div>
    );
  }

  const aiActive = !lead.aiPaused;
  const events = lead.threadEvents ?? [];

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div
        className="px-4 py-3 flex items-center gap-3 flex-shrink-0"
        style={{ borderBottom: '1px solid #F0F3F8' }}
      >
        {/* Mobile back button */}
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-[12px] font-medium text-muted hover:text-ink transition-colors flex-shrink-0 mr-1"
            aria-label="Back to conversations"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Back
          </button>
        )}

        <AvatarInitials name={`${lead.firstName} ${lead.lastName}`} size={34} />
        <div className="flex-1 min-w-0">
          <p
            className="text-[14px] font-semibold text-ink truncate"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            {lead.firstName} {lead.lastName}
          </p>
          <p className="text-[11px] text-muted truncate">
            {lead.phone} · {lead.state} · {lead.source} ·{' '}
            <span style={{ fontFamily: '"DM Mono", monospace' }}>
              {fmtTime(lead.createdAt, timezone)}
            </span>
          </p>
        </div>

        {/* Stage select — interactive (MAJOR #9) */}
        <StageSelect leadId={leadId} stage={lead.stage as LeadStage} />

        {/* AI toggle */}
        <AiToggle
          aiActive={aiActive}
          onChange={(ai) => toggleAi.mutate(ai)}
        />

        {/* Rail toggle — hidden on mobile (when onBack is set) */}
        {!onBack && (
          <button
            onClick={onToggleRail}
            className="p-1.5 rounded-[8px] text-muted hover:bg-surface-alt transition-colors"
            title={showRail ? 'Hide detail rail' : 'Show detail rail'}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
              <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="15" y1="3" x2="15" y2="21" />
            </svg>
          </button>
        )}
      </div>

      {/* Thread scroll */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-1">
        {events.map((ev) => (
          <ThreadEventRow
            key={ev.id}
            ev={ev}
            leadId={leadId}
            aiName={aiName}
            timezone={timezone}
          />
        ))}

        {events.length === 0 && (
          <div className="text-center text-[12.5px] text-muted mt-8">
            No messages yet — thread starts when Ava makes contact.
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="px-4 py-3 flex-shrink-0" style={{ borderTop: '1px solid #F0F3F8' }}>
        {!aiActive && (
          <div
            className="mb-2 px-3 py-1.5 rounded-[8px] text-[11px] font-medium"
            style={{ background: 'rgba(251,191,36,0.08)', color: '#B45309', border: '1px solid rgba(251,191,36,0.2)' }}
          >
            Manual takeover — {aiName} is paused on this lead…
          </div>
        )}

        <ComingSoon label="Text channel coming soon">
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-chip"
            style={{
              border: '1px solid #E5EAF2',
              background: aiActive ? '#F9FAFB' : 'white',
              opacity: aiActive ? 0.5 : 1,
              pointerEvents: aiActive ? 'none' : 'auto',
            }}
          >
            <button className="text-muted">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <input
              className="flex-1 text-[13.5px] bg-transparent outline-none text-muted"
              placeholder={
                aiActive
                  ? `${aiName} is handling this conversation — switch to Manual to jump in`
                  : 'Type a message…'
              }
              disabled={aiActive}
            />
            <button className="text-muted text-base">😊</button>
            <button
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#2E66E0' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          </div>
        </ComingSoon>
      </div>
    </div>
  );
}

// ── Detail Rail ───────────────────────────────────────────────────────────────

function DetailRail({ leadId, aiName, timezone }: { leadId: string; aiName: string; timezone: string }) {
  const { data: lead } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => fetchLead(leadId),
  });

  if (!lead) return null;

  const tel = lead.telIntel;
  const channelColor = tel ? CHANNEL_COLOR[tel.channel] ?? '#64748B' : '#64748B';

  const QUAL_ITEMS = [
    { label: 'Owns home', status: 'confirmed', note: '' },
    { label: 'State licensed', status: 'confirmed', note: '' },
    { label: 'Age band', status: 'confirmed', note: '' },
    { label: 'Mortgage balance', status: 'pending', note: '' },
    { label: 'Homeowner status', status: lead.customFields?.['beneficiary'] ? 'confirmed' : 'pending', note: '' },
  ];

  return (
    <div
      className="flex flex-col gap-4 overflow-y-auto py-4 px-3"
      style={{ borderLeft: '1px solid #F0F3F8' }}
    >
      {/* Deal value */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-1">Deal value</p>
        <p
          className="text-[22px] font-medium text-ink"
          style={{ fontFamily: '"DM Mono", monospace' }}
        >
          ${lead.dealValue.toLocaleString()}
        </p>
      </div>

      {/* Contact info */}
      <div className="bg-surface rounded-card p-3 shadow-card flex flex-col gap-2">
        {[
          { label: 'Source', val: lead.source },
          { label: 'State', val: lead.state ?? '—' },
          { label: 'Local time', val: fmtTime(new Date().toISOString(), timezone) },
          { label: 'Email', val: lead.email ?? '—' },
          { label: 'Received', val: fmtTime(lead.createdAt, timezone) },
        ].map(({ label, val }) => (
          <div key={label} className="flex items-center gap-2">
            <span className="text-[10.5px] text-muted w-20 flex-shrink-0">{label}</span>
            <span className="text-[11px] text-ink font-medium truncate">{val}</span>
          </div>
        ))}
      </div>

      {/* Phone intel */}
      {tel && (
        <div className="bg-surface rounded-card p-3 shadow-card">
          <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-2">Phone intel</p>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-chip text-[10px] font-semibold mb-2"
            style={{ background: `${channelColor}18`, color: channelColor }}
          >
            {tel.channel.toUpperCase()}
          </span>
          {[
            { label: 'Line type', val: tel.line_type },
            { label: 'Carrier', val: tel.carrier },
            { label: 'Device', val: tel.device },
          ].map(({ label, val }) => (
            <div key={label} className="flex items-center gap-2 mt-1.5">
              <span className="text-[10.5px] text-muted w-20 flex-shrink-0">{label}</span>
              <span className="text-[11px] text-ink font-medium">{val}</span>
            </div>
          ))}
          {tel.line_type === 'Landline' && (
            <div
              className="mt-2 px-2 py-1.5 rounded-[8px] text-[10.5px]"
              style={{ background: 'rgba(251,191,36,0.08)', color: '#B45309' }}
            >
              Landline — {aiName} skips SMS steps and works this lead by voice only.
            </div>
          )}
        </div>
      )}

      {/* Qualification checklist */}
      <div className="bg-surface rounded-card p-3 shadow-card">
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-2">Qualification</p>
        <div className="flex flex-col gap-1.5">
          {QUAL_ITEMS.map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background:
                    item.status === 'confirmed' ? 'rgba(21,150,98,0.1)' :
                      item.status === 'failed' ? 'rgba(239,68,68,0.1)' : '#F2F4F7',
                }}
              >
                {item.status === 'confirmed' && (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#159662" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {item.status === 'failed' && (
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                )}
              </div>
              <span className="text-[11px] text-ink">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Appointments card — greyed (V1) */}
      <ComingSoon label="Booking — coming soon">
        <div className="bg-surface rounded-card p-3 shadow-card">
          <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-2">Appointments</p>
          <p className="text-[12px] text-muted">No appointments yet</p>
          <button
            className="mt-2 text-[11px] font-semibold flex items-center gap-1"
            style={{ color: '#6D4AEA' }}
          >
            <span>✦</span> Have {aiName} reschedule
          </button>
        </div>
      </ComingSoon>

      {/* Custom fields */}
      {Object.keys(lead.customFields ?? {}).length > 0 && (
        <div className="bg-surface rounded-card p-3 shadow-card">
          <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted mb-2">Custom fields</p>
          {Object.entries(lead.customFields ?? {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 mt-1.5">
              <span className="text-[10.5px] text-muted w-20 flex-shrink-0 truncate">{k}</span>
              <span className="text-[11px] text-ink font-medium truncate">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Inbox Page ───────────────────────────────────────────────────────────

interface InboxPageProps {
  aiName: string;
}

export function InboxPage({ aiName }: InboxPageProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRail, setShowRail] = useState(true);
  const isMobile = useIsMobile();

  // Get workspace timezone from cached settings (fetchSettings is also called in AppShell)
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettings,
    staleTime: 5 * 60 * 1000,
  });
  const timezone = (settings as { timezone?: string } | undefined)?.timezone ?? 'America/Chicago';

  // Mobile: view toggles between list and thread
  const [mobileView, setMobileView] = useState<'list' | 'thread'>('list');

  const handleSelectLead = (id: string) => {
    setSelectedId(id);
    if (isMobile) setMobileView('thread');
  };

  const handleBack = () => {
    setMobileView('list');
  };

  // Desktop: always show all panes based on selectedId
  if (!isMobile) {
    return (
      <div className="flex" style={{ height: '100%' }}>
        {/* Conversation list */}
        <div className="flex-shrink-0 overflow-hidden" style={{ width: 280 }}>
          <ConvList selectedId={selectedId} onSelect={setSelectedId} timezone={timezone} />
        </div>

        {/* Thread */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {selectedId ? (
            <ThreadPane
              leadId={selectedId}
              aiName={aiName}
              showRail={showRail}
              onToggleRail={() => setShowRail((r) => !r)}
              timezone={timezone}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <AiOrb size={36} pulse={false} />
              <p className="text-[13.5px] text-muted">Select a conversation to open the thread</p>
            </div>
          )}
        </div>

        {/* Detail rail */}
        {selectedId && showRail && (
          <div className="flex-shrink-0 overflow-hidden" style={{ width: 248 }}>
            <DetailRail leadId={selectedId} aiName={aiName} timezone={timezone} />
          </div>
        )}
      </div>
    );
  }

  // Mobile: single-pane (list XOR thread, no detail rail)
  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      {mobileView === 'list' || !selectedId ? (
        /* Full-width conversation list */
        <div style={{ height: '100%' }}>
          <ConvList selectedId={selectedId} onSelect={handleSelectLead} timezone={timezone} />
        </div>
      ) : (
        /* Full-screen thread with ← back button */
        <div style={{ height: '100%' }}>
          <ThreadPane
            leadId={selectedId}
            aiName={aiName}
            showRail={false}
            onToggleRail={() => {}}
            onBack={handleBack}
            timezone={timezone}
          />
        </div>
      )}
    </div>
  );
}
